import { DreamBlock, Node } from '@typedream/core';
import { IPagePaths } from '@typedream/data';
import { COLLECTION_CONNECT_ITEM_PAGE_URL, DEFAULT_COLLECTION_DATA_ID, ITEM_PAGE_DEFAULT_FIELDS } from '../constants';
import { BLOCK_ELEMENT_KEY } from '../nodes/collection/components/CollectionDataElement/types';
import {
  ICollectionData,
  ICollectionDataItem,
  ICollectionPageData, ICollectionPageDataItem, IOptionsElement,
} from '../nodes/collection/types';
import { DreamElement } from '../plate';
import { ELEMENT_COLLECTION, ELEMENT_COLLECTION_ITEM, ELEMENT_IMAGE } from '../pluginKeys';

const IMAGE_PLACEHOLDER_URL = '/images/image-placeholder.svg';

const updateCollectionItem = <T extends DreamElement>(
  data: { [key: string]: any },
  schema: { [key: string]: any },
  element: T,
  pageID: string,
  pagePath: string[],
): T => {
  const newElement = { ...element };
  if (element?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA]) {
    (element as IOptionsElement)[
      BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS
    ]?.forEach((connectKeys) => {
      const { schemaKey, blockElementKey } = connectKeys;
      const dataFieldName = schema[schemaKey];
      const currData = data[dataFieldName];
      if (currData) {
        // turns out if blockElementKey is text and currData is number, not string, crash
        (newElement as any)[blockElementKey] = `${currData}`;
      } else if (dataFieldName === COLLECTION_CONNECT_ITEM_PAGE_URL) {
        const itemSlug = data[
          ITEM_PAGE_DEFAULT_FIELDS.SLUG.fieldName];
        newElement[BLOCK_ELEMENT_KEY.COLLECTION_LINK_SLUG] = itemSlug;
        newElement[BLOCK_ELEMENT_KEY.COLLECTION_LINK_DATA_ID] = data[DEFAULT_COLLECTION_DATA_ID];
        newElement[BLOCK_ELEMENT_KEY.COLLECTION_INTERNAL_LINK] = true;
        (newElement as any)[BLOCK_ELEMENT_KEY.INTERNAL_LINK] = true;
        (newElement as any)[BLOCK_ELEMENT_KEY.PAGE_ID] = pageID;
        (newElement as any)[BLOCK_ELEMENT_KEY.URL] = pagePath ? `/${pagePath?.join('/')}` : '';
      } else if (
        !currData
          && ((element.type === ELEMENT_IMAGE && blockElementKey === BLOCK_ELEMENT_KEY.URL)
          || blockElementKey === BLOCK_ELEMENT_KEY.BACKGROUND_IMAGE)
      ) {
        (newElement as any)[blockElementKey] = IMAGE_PLACEHOLDER_URL;
      } else {
        (newElement as any)[blockElementKey] = '';
      }
    });
  }
  if (element?.children) {
    const elementChildren = [...element.children];
    const newElementChildren = elementChildren.map(
      (child) => updateCollectionItem(data, schema, child as any, pageID, pagePath),
    );
    if (newElementChildren) {
      newElement.children = newElementChildren;
    }
  }
  return newElement;
};
const updateCollection = (
  collectionData: ICollectionData,
  spaceCollectionData: ICollectionPageData,
  pagesPaths: IPagePaths,
  getItemPageDataID: (collDataItem: ICollectionDataItem) => string,
  elementID?: string,
) => <T extends DreamBlock>(obj: T): T => {
  const localObj = { ...obj };

  const elementIDPass = elementID ? localObj.id === elementID : true;
  if (localObj.type === ELEMENT_COLLECTION && elementIDPass) {
    const dataID = localObj?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    let collection = localObj?.children;
    if (dataID) {
      const currCollData = collectionData[dataID];
      const itemPageDataID = getItemPageDataID(currCollData);
      const currSpaceCollData: ICollectionPageDataItem = spaceCollectionData[itemPageDataID];
      const currData = currCollData?.data;
      const template = localObj?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE]?.template;
      const schema = localObj?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE]?.schema;
      if (currData) {
        collection = currData.map((row) => {
          const collectionItem = updateCollectionItem(
            row,
            schema,
            template as DreamElement,
            currSpaceCollData?.childrenTemplatePageID,
            pagesPaths[currSpaceCollData?.childrenTemplatePageID]?.path,
          );
          return ({
            children: [
              collectionItem,
            ],
            [BLOCK_ELEMENT_KEY.COLLECTION_ITEM_PAGE_ID]: currSpaceCollData?.childrenTemplatePageID,
            [BLOCK_ELEMENT_KEY.COLLECTION_ITEM_ID]: row[DEFAULT_COLLECTION_DATA_ID],
            type: ELEMENT_COLLECTION_ITEM,
          });
        });
        if (!collection || collection?.length <= 0) {
          collection = [{
            text: '',
          }];
        }
      }
      localObj.children = collection;
    }
  }

  const localChildren = Node.isNode(localObj) && localObj.children
    ? localObj.children.map(updateCollection(
      collectionData,
      spaceCollectionData,
      pagesPaths,
      getItemPageDataID,
      elementID,
    ) as any) as DreamBlock[]
    : undefined;

  if (localChildren) {
    localObj.children = localChildren;
  }

  return localObj;
};
export default updateCollection;
